/* eslint-disable @typescript-eslint/no-explicit-any */
import { LoggedUser, Team, User } from 'entities';

import { AnyFunction, AnyObject, UnionToIntersection } from 'types';
import { omit } from 'utils/object';

export type RoutePermissions = Partial<Pick<User, 'type' | 'isSysAdmin'>> & {
  team?: Partial<
    Pick<
      Team,
      | 'reprice'
      | 'outsourcing'
      | 'taskManagement'
      | 'addTaskSubOwner'
      | 'hasServices'
      | 'salesArea'
      | 'canAssumeTasksFromOtherAreas'
      | 'canAssumeProject'
      | 'canRequestTaskReasignement'
      | 'canSetDeadlines'
      | 'canChangePrices'
      | 'isProjectOwner'
      | 'canCreateProject'
      | 'isRecruitmentTeam'
      | 'canApproveCountryAddition'
      | 'canEnableProjectToStart'
      | 'isInvoiceTeam'
      | 'canApproveDiscount'
    >
  >;
};

type RouteConfig = {
  /**
   * The route path
   *
   * It will be prefixed with every parent above the `children` tree
   */
  path: string;

  /**
   * If the route is no-auth access only
   */
  isOpen?: boolean;

  children?: Record<string, RouteConfig>;

  title?: string;

  /**
   * If its a dynamic route (/:example), this is required
   * Define the parameters it needs to navigate there
   *
   * Important: Nested children do NOT need to redefine its parent's params
   */
  getPath?: (params: any) => string;

  /**
   * All the params we can use to redirect to the route
   *
   * These will be treated as optional
   *
   * If passing the array directly, cast it as const
   *
   * You dont need to define each and every query param here,
   * just the ones that could be relevant to other pages to reference
   *
   */
  queryParams?: string[];

  permissions?: RoutePermissions;
  customPermissionCheck?: (user: LoggedUser) => boolean;
};

// type FunctionParamMerge<Fn, Params> = Fn extends AnyFunction ? Params & Parameters<Fn>[0] : Params;

type FunctionParamMerge<Fn, Params> = Fn extends AnyFunction
  ? Params extends undefined
    ? Parameters<Fn>[0]
    : Parameters<Fn>[0] & Params
  : Params;

type RouteQueryParams<Config extends RouteConfig> = Config['queryParams'] extends string[]
  ? { [Key in Config['queryParams'][number]]?: string }
  : object;

type RoutePathParams<Config extends RouteConfig, ParentPathParams = undefined> =
  ParentPathParams extends Record<string, string>
    ? {
        getPath: (
          params: FunctionParamMerge<Config['getPath'], ParentPathParams> &
            RouteQueryParams<Config>,
        ) => string;

        /**
         * WARNING! This is a type helper only
         */
        isDynamic: true;
      }
    : Config['getPath'] extends AnyFunction
      ? {
          getPath: (
            params: FunctionParamMerge<Config['getPath'], RouteQueryParams<Config>>,
          ) => string;

          /**
           * WARNING! This is a type helper only
           */
          isDynamic: true;
        }
      : Config['queryParams'] extends string[]
        ? { getPath: (params?: RouteQueryParams<Config>) => string }
        : object;

// We use omit as Pick would produce bad results
type SingleRoute<Config extends RouteConfig, ParentPathParams = undefined> = Omit<
  Config,
  'children' | 'getPath' | 'queryParams'
> &
  RoutePathParams<Config, ParentPathParams>;

export type BaseRoute = SingleRoute<RouteConfig, any>;

type FlattenRouteInner<
  T extends Record<string, RouteConfig>,
  Key = keyof T,
  ParentPathParams = undefined,
> = Key extends string
  ? T[Key]['children'] extends Record<string, RouteConfig>
    ?
        | FlattenRouteInner<
            T[Key]['children'],
            keyof T[Key]['children'],
            FunctionParamMerge<T[Key]['getPath'], ParentPathParams>
          >
        | { [K in Key]: SingleRoute<T[K], ParentPathParams> }
    : { [K in Key]: SingleRoute<T[K], ParentPathParams> }
  : never;

type FlattenRoute<T extends Record<string, RouteConfig>> = UnionToIntersection<
  FlattenRouteInner<T>
>;

function mergePaths(leftPath: string, rightPath: string): string {
  const [leftPieces, rightPieces] = [leftPath, rightPath].map((path) =>
    path.split('/').filter(Boolean),
  );

  const pathPieces = [...leftPieces, ...rightPieces];

  return `/${pathPieces.join('/')}`;
}

function adjustChildRoute(parent: RouteConfig, child: RouteConfig): RouteConfig {
  const shouldGetPath = !!(parent.getPath || child.getPath);

  return {
    ...child,

    path: mergePaths(parent.path, child.path),

    getPath: shouldGetPath
      ? (params) =>
          mergePaths(parent.getPath?.(params) || parent.path, child.getPath?.(params) || child.path)
      : undefined,
  };
}

function extractChildren(parent: RouteConfig): Record<string, RouteConfig> {
  if (!parent.children) return {};

  return Object.fromEntries(
    Object.entries(parent.children).map(([key, child]) => [key, adjustChildRoute(parent, child)]),
  );
}

function validateDuplicateKey(aggregate: AnyObject, toMerge: AnyObject): void {
  const duplicateKey = Object.keys(toMerge).find((key) => key in aggregate);

  if (duplicateKey) throw new Error(`Duplicate key "${duplicateKey}" found on routing`);
}

function buildQueryParams(routeParams: AnyObject = {}, queryParams: string[] = []): string {
  const paramsPresent = queryParams.filter((param) => param in routeParams);

  if (!paramsPresent.length) return '';

  const params = new URLSearchParams(paramsPresent.map((param) => [param, routeParams[param]]));

  return `?${params.toString()}`;
}

function transformRoute({
  queryParams,
  getPath,
  path,

  ...config
}: RouteConfig): SingleRoute<RouteConfig, any> {
  return {
    ...omit(config, ['children']),

    path,

    getPath: (params = {}) =>
      `${getPath?.(params) ?? path}${buildQueryParams(params, queryParams)}`,
  };
}

export function createRoutes<Routes extends Record<string, RouteConfig>>(
  routes: Routes,
): FlattenRoute<Routes> {
  let flatten = {} as FlattenRoute<Routes>;

  Object.entries(routes).forEach(([key, route]) => {
    const parsedRoute = transformRoute(route);

    const children = route.children ? createRoutes(extractChildren(route)) : {};

    const newRoutes = { ...children, [key]: parsedRoute };

    validateDuplicateKey(flatten, newRoutes);

    flatten = {
      ...flatten,
      ...newRoutes,
    };
  });

  return flatten;
}
